import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const HomePage = () => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    document.querySelectorAll('.animate-on-scroll').forEach(el => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const navigateToLogin = (role) => {
    switch (role) {
      case 'student':
        navigate('/student-login');
        break;
      case 'faculty': // Corrected to match the button's role
        navigate('/staff-login');
        break;
      case 'admin':
      case 'evaluation':
        navigate('/admin-login');
        break;
      default:
        console.warn(`Unknown login role: ${role}`);
        break;
    }
  };

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: 'smooth'
    });
  };

  return (
    <div className="wiser-template">
      {/* Navigation - Updated with new menu items */}
      <nav className="wiser-navbar">
        <div className="container">
          <div className="navbar-content">
            <div className="navbar-brand">
              <i className="fas fa-building"></i>
              <span>ERP Portal</span>
            </div>
            
            <button 
              className="mobile-toggle"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
            >
              <span></span>
              <span></span>
              <span></span>
            </button>

            <ul className={`navbar-nav ${showMobileMenu ? 'mobile-active' : ''}`}>
              <li><a onClick={() => scrollToSection('home')}>Home</a></li>
              <li><a onClick={() => scrollToSection('about')}>About Us</a></li>
              <li><a onClick={() => scrollToSection('contact')}>Contact Us</a></li>
              <li><a onClick={() => navigate('/student-enquiry')}>Enquiry</a></li>
              <li><a onClick={() => navigateToLogin('student')}>Student</a></li>
              <li><a onClick={() => navigateToLogin('faculty')}>Staff</a></li>
              <li><a onClick={() => navigateToLogin('admin')}>Admin</a></li> 
              <li><a onClick={() => navigateToLogin('evaluation')}>Evaluation</a></li> 
            </ul>
          </div>
        </div>
      </nav>

      {/* Hero Section - Updated branding */}
      <section className="wiser-hero" id="home">
        <div className="hero-overlay"></div>
        <div className="container">
          <div className="hero-content animate-on-scroll">
            <div className="hero-badge">
              <span>Welcome to Educational Excellence</span>
            </div>
            <h1 className="hero-title">
              Empowering Education Through 
              <span className="text-highlight"> Smart Technology</span>
            </h1>
            <p className="hero-description">
              Experience the future of university management with our comprehensive 
              ERP portal designed for students, faculty, and administrators.
            </p>
            <div className="hero-buttons">
              <button className="btn btn-primary btn-lg" onClick={() => scrollToSection('courses')}>
                Explore Portal
              </button>
              <button className="btn btn-outline btn-lg" onClick={() => scrollToSection('about')}>
                Learn More
              </button>
            </div>
          </div>
          <div className="hero-stats animate-on-scroll">
            <div className="stat-item">
              <h3>50K+</h3>
              <p>Students</p>
            </div>
            <div className="stat-item">
              <h3>2K+</h3>
              <p>Faculty</p>
            </div>
            <div className="stat-item">
              <h3>100+</h3>
              <p>Courses</p>
            </div>
            <div className="stat-item">
              <h3>24/7</h3>
              <p>Support</p>
            </div>
          </div>
        </div>
      </section>

      {/* Portal Access Section - Fixed UI issues */}
      <section className="wiser-courses" id="courses">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Portal Access</span>
            <h2 className="section-title">Choose Your Portal</h2>
            <p className="section-description">
              Access your dedicated portal with role-based features and functionality
            </p>
          </div>
          
          <div className="courses-grid">
            <div className="course-card animate-on-scroll">
              <div className="course-image student-portal">
                <i className="fas fa-user-graduate"></i>
                <div className="course-overlay">
                  <a 
                    href="/student-login" 
                    className="btn btn-white" 
                    onClick={(e) => { e.preventDefault(); navigateToLogin('student'); }}
                  >
                    Access Portal
                  </a>
                </div>
              </div>
              <div className="course-content">
                <div className="course-category">Students</div>
                <h3>Student Portal</h3>
                <p>Access grades, schedules, assignments, and academic resources in one place.</p>
                <div className="course-features">
                  <span><i className="fas fa-check"></i> Grade Management</span>
                  <span><i className="fas fa-check"></i> Course Registration</span>
                  <span><i className="fas fa-check"></i> Academic Calendar</span>
                </div>
              </div>
            </div>

            <div className="course-card animate-on-scroll">
              <div className="course-image faculty-portal">
                <i className="fas fa-chalkboard-teacher"></i>
                <div className="course-overlay">
                  <a 
                    href="/staff-login" 
                    className="btn btn-white" 
                    onClick={(e) => { e.preventDefault(); navigateToLogin('faculty'); }}
                  >
                    Access Portal
                  </a>
                </div>
              </div>
              <div className="course-content">
                <div className="course-category">Faculty</div>
                <h3>Faculty Portal</h3>
                <p>Manage courses, student assessments, and academic administrative tasks.</p>
                <div className="course-features">
                  <span><i className="fas fa-check"></i> Course Management</span>
                  <span><i className="fas fa-check"></i> Student Assessment</span>
                  <span><i className="fas fa-check"></i> Academic Reports</span>
                </div>
              </div>
            </div>

            <div className="course-card animate-on-scroll">
              <div className="course-image admin-portal">
                <i className="fas fa-user-tie"></i>
                <div className="course-overlay">
                  <a 
                    href="/admin-login" 
                    className="btn btn-white" 
                    onClick={(e) => { e.preventDefault(); navigateToLogin('admin'); }}
                  >
                    Access Portal
                  </a>
                </div>
              </div>
              <div className="course-content">
                <div className="course-category">Administration</div>
                <h3>Admin Portal</h3>
                <p>Complete system administration, user management, and institutional oversight.</p>
                <div className="course-features">
                  <span><i className="fas fa-check"></i> System Management</span>
                  <span><i className="fas fa-check"></i> User Administration</span>
                  <span><i className="fas fa-check"></i> Reports & Analytics</span>
                </div>
              </div>
            </div>

            <div className="course-card animate-on-scroll">
              <div className="course-image evaluation-portal">
                <i className="fas fa-clipboard-check"></i>
                <div className="course-overlay">
                  <a 
                    href="/admin-login" 
                    className="btn btn-white" 
                    onClick={(e) => { e.preventDefault(); navigateToLogin('evaluation'); }}
                  >
                    Access Portal
                  </a>
                </div>
              </div>
              <div className="course-content">
                <div className="course-category">Evaluation</div>
                <h3>Evaluation Portal</h3>
                <p>Comprehensive assessment and evaluation management for academic excellence.</p>
                <div className="course-features">
                  <span><i className="fas fa-check"></i> Assessment Tools</span>
                  <span><i className="fas fa-check"></i> Result Processing</span>
                  <span><i className="fas fa-check"></i> Performance Analytics</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section - Features Style */}
      <section className="wiser-about" id="about">
        <div className="container">
          <div className="about-content">
            <div className="about-text animate-on-scroll">
              <span className="section-badge">About Our System</span>
              <h2>Comprehensive University Management Solution</h2>
              <p>
                Our ERP portal revolutionizes educational administration by providing 
                integrated solutions for all stakeholders in the academic ecosystem.
              </p>
              <div className="features-list">
                <div className="feature-item">
                  <i className="fas fa-graduation-cap"></i>
                  <div>
                    <h4>Academic Excellence</h4>
                    <p>Streamlined academic processes for enhanced learning outcomes</p>
                  </div>
                </div>
                <div className="feature-item">
                  <i className="fas fa-chart-line"></i>
                  <div>
                    <h4>Performance Analytics</h4>
                    <p>Data-driven insights for informed decision making</p>
                  </div>
                </div>
                <div className="feature-item">
                  <i className="fas fa-shield-alt"></i>
                  <div>
                    <h4>Secure Platform</h4>
                    <p>Enterprise-grade security for all your academic data</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="about-image animate-on-scroll">
              <div className="image-placeholder">
                <i className="fas fa-university"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* System Modules Section */}
      <section className="wiser-modules">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">System Modules</span>
            <h2 className="section-title">Powerful Features</h2>
            <p className="section-description">
              Discover the comprehensive modules that make our ERP system complete
            </p>
          </div>
          
          <div className="modules-grid">
            <div className="module-card animate-on-scroll">
              <div className="module-icon">
                <i className="fas fa-user-graduate"></i>
              </div>
              <h3>Student Management</h3>
              <ul>
                <li>Profile Management</li>
                <li>Course Registration</li>
                <li>Academic Records</li>
                <li>Progress Tracking</li>
              </ul>
            </div>

            <div className="module-card animate-on-scroll">
              <div className="module-icon">
                <i className="fas fa-calendar-alt"></i>
              </div>
              <h3>Examination System</h3>
              <ul>
                <li>Exam Scheduling</li>
                <li>Seat Allocation</li>
                <li>Result Processing</li>
                <li>Grade Management</li>
              </ul>
            </div>

            <div className="module-card animate-on-scroll">
              <div className="module-icon">
                <i className="fas fa-certificate"></i>
              </div>
              <h3>Result Processing</h3>
              <ul>
                <li>Grade Sheet Generation</li>
                <li>Transcript Management</li>
                <li>Degree Certificates</li>
                <li>Academic Analytics</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Faculty Section */}
      <section className="wiser-faculty" id="faculty">
        <div className="container">
          <div className="faculty-content animate-on-scroll">
            <span className="section-badge">Faculty Excellence</span>
            <h2>Empowering Educators</h2>
            <p>
              Our platform provides faculty members with cutting-edge tools to enhance 
              teaching effectiveness and streamline administrative tasks.
            </p>
            <div className="faculty-stats">
              <div className="faculty-stat">
                <h3>2000+</h3>
                <p>Active Faculty</p>
              </div>
              <div className="faculty-stat">
                <h3>500+</h3>
                <p>Departments</p>
              </div>
              <div className="faculty-stat">
                <h3>95%</h3>
                <p>Satisfaction Rate</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="wiser-contact" id="contact">
        <div className="container">
          <div className="contact-content">
            <div className="contact-info animate-on-scroll">
              <span className="section-badge">Get In Touch</span>
              <h2>Contact Information</h2>
              <div className="contact-items">
                <div className="contact-item">
                  <i className="fas fa-map-marker-alt"></i>
                  <div>
                    <h4>Campus Address</h4>
                    <p>University Campus, Education Street<br />Academic City, State 12345</p>
                  </div>
                </div>
                <div className="contact-item">
                  <i className="fas fa-phone"></i>
                  <div>
                    <h4>Phone Number</h4>
                    <p>+91 98765 43210<br />+91 87654 32109</p>
                  </div>
                </div>
                <div className="contact-item">
                  <i className="fas fa-envelope"></i>
                  <div>
                    <h4>Email Address</h4>
                    <p>info@universityerp.edu<br />support@universityerp.edu</p>
                  </div>
                </div>
                <div className="contact-item">
                  <i className="fas fa-clock"></i>
                  <div>
                    <h4>Office Hours</h4>
                    <p>Mon - Fri: 9:00 AM - 6:00 PM<br />Sat: 9:00 AM - 2:00 PM</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="contact-form animate-on-scroll">
              <h3>Send us a Message</h3>
              <div className="contact-form-wrapper">
                <div className="form-group">
                  <input type="text" placeholder="Your Name" />
                </div>
                <div className="form-group">
                  <input type="email" placeholder="Your Email" />
                </div>
                <div className="form-group">
                  <input type="text" placeholder="Subject" />
                </div>
                <div className="form-group">
                  <textarea placeholder="Your Message" rows="5"></textarea>
                </div>
                <button type="button" className="btn btn-primary btn-block">
                  Send Message
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Updated branding */}
      <footer className="wiser-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-brand">
                <i className="fas fa-building"></i>
                <span>ERP Portal</span>
              </div>
              <p>Transforming education through innovative technology solutions.</p>
              <div className="social-links">
                <a href="#"><i className="fab fa-facebook"></i></a>
                <a href="#"><i className="fab fa-twitter"></i></a>
                <a href="#"><i className="fab fa-linkedin"></i></a>
                <a href="#"><i className="fab fa-instagram"></i></a>
              </div>
            </div>
            <div className="footer-section">
              <h4>Quick Links</h4>
              <ul>
                <li><a href="#home">Home</a></li>
                <li><a href="#about">About</a></li>
                <li><a href="#courses">Portals</a></li>
                <li><a href="#faculty">Faculty</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Support</h4>
              <ul>
                <li><a href="#">Help Center</a></li>
                <li><a href="#">Documentation</a></li>
                <li><a href="#">System Status</a></li>
                <li><a href="#">Contact Support</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Contact Info</h4>
              <p><i className="fas fa-phone"></i> +91 98765 43210</p>
              <p><i className="fas fa-envelope"></i> info@universityerp.edu</p>
              <p><i className="fas fa-map-marker-alt"></i> Education Street, Academic City</p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} ERP Portal. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <link 
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css" 
        rel="stylesheet" 
      />
    </div>
  );
};

export default HomePage;